import { z } from "zod"

/**
 * Партнёры и партнёрские промокоды (26.09.2026).
 *
 * Промокод площадки получил владельца (партнёра) и аудиторию: покупательский
 * код вводят при оформлении заказа, продавцовый — при регистрации в кабинете
 * продавца. Здесь — справочник партнёров, статистика кода и партнёра и
 * привязка кода к магазину. Выплат партнёрам нет: «начислено» — расчётная
 * цифра по условию вознаграждения.
 */

export const PromoAudienceSchema = z.enum(["buyer", "seller"])
export type PromoAudience = z.infer<typeof PromoAudienceSchema>

/**
 * Вознаграждение партнёра — только процент (решение заказчика 26.09.2026).
 * Покупательский код: % от стоимости товаров после скидки за вычетом
 * возвратов, без доставки. Продавцовый: % от комиссии площадки с продаж
 * привлечённого продавца за вычетом возвратов. Фиксированных сумм нет.
 */
export const PromoRewardKindSchema = z.enum(["none", "percent"], {
  message: "Вознаграждение партнёра задаётся только процентом",
})
export type PromoRewardKind = z.infer<typeof PromoRewardKindSchema>

/** Состояние кода «на сейчас» — выводится из выключателя, срока и лимита. */
export const PromoStateSchema = z.enum(["active", "inactive", "scheduled", "expired", "exhausted", "archived"])
export type PromoState = z.infer<typeof PromoStateSchema>

export const PartnerKindSchema = z.enum([
  "blogger",
  "stylist",
  "ambassador",
  "employee",
  "agency",
  "seller",
  "other",
])
export type PartnerKind = z.infer<typeof PartnerKindSchema>

export const PartnerRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: PartnerKindSchema,
})
export type PartnerRef = z.infer<typeof PartnerRefSchema>

/** Ключевые цифры кода для списка (за всё время). */
export const PromoSummarySchema = z.object({
  /** Засчитанные заказы (оплачены, не отменены). */
  orders: z.number().int(),
  buyers: z.number().int(),
  /** Товары до скидки. */
  gmv: z.number(),
  discount: z.number(),
  /** Привлечённые продавцы (завершили регистрацию) и прошедшие модерацию. */
  sellers: z.number().int(),
  sellersApproved: z.number().int(),
  /** Продажи привлечённых продавцов (товары). */
  sellerGmv: z.number(),
  /** Начислено партнёру (расчётно). */
  reward: z.number(),
})
export type PromoSummary = z.infer<typeof PromoSummarySchema>

export const PartnerLinkedUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  shopName: z.string().nullable(),
})
export type PartnerLinkedUser = z.infer<typeof PartnerLinkedUserSchema>

export const PartnerSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: PartnerKindSchema,
  contactName: z.string(),
  phone: z.string(),
  email: z.string(),
  telegram: z.string(),
  note: z.string(),
  userId: z.string().nullable(),
  user: PartnerLinkedUserSchema.nullable(),
  active: z.boolean(),
  promoCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Partner = z.infer<typeof PartnerSchema>

export const PartnerInputSchema = z.object({
  name: z.string().trim().min(1).max(160),
  kind: PartnerKindSchema.default("blogger"),
  contactName: z.string().trim().max(160).default(""),
  phone: z.string().trim().max(40).default(""),
  email: z.string().trim().max(160).default(""),
  telegram: z.string().trim().max(80).default(""),
  note: z.string().trim().max(2000).default(""),
  userId: z.string().nullable().default(null),
  active: z.boolean().default(true),
})
export type PartnerInput = z.infer<typeof PartnerInputSchema>

export const PartnerPatchSchema = PartnerInputSchema.partial()
export type PartnerPatch = z.infer<typeof PartnerPatchSchema>

/** Статус заказа для статистики — пять понятных групп вместо 18 статусов. */
export const PromoOrderStatusGroupSchema = z.enum([
  "awaiting",
  "paid",
  "delivered",
  "returned",
  "cancelled",
])
export type PromoOrderStatusGroup = z.infer<typeof PromoOrderStatusGroupSchema>

export const PromoStatsOrderSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  status: z.string(),
  statusGroup: PromoOrderStatusGroupSchema,
  buyerId: z.string().nullable(),
  buyerName: z.string(),
  /** Телефон маской: +7 916 ***-**-00. */
  buyerPhone: z.string(),
  /** Первый засчитанный заказ покупателя на площадке. */
  isNewBuyer: z.boolean(),
  itemCount: z.number().int(),
  subtotal: z.number(),
  discount: z.number(),
  /** Товары после скидки. */
  total: z.number(),
  delivery: z.number(),
  refund: z.number(),
  counted: z.boolean(),
})
export type PromoStatsOrder = z.infer<typeof PromoStatsOrderSchema>

export const PromoStatsBuyerSchema = z.object({
  buyerId: z.string().nullable(),
  name: z.string(),
  phone: z.string(),
  orders: z.number().int(),
  revenue: z.number(),
  discount: z.number(),
  firstOrderAt: z.string().nullable(),
  isNew: z.boolean(),
  /** Пришёл по ссылке/коду этого промокода (регистрация или первый заказ). */
  attributed: z.boolean(),
})
export type PromoStatsBuyer = z.infer<typeof PromoStatsBuyerSchema>

export const PromoStatsProductSchema = z.object({
  productId: z.string(),
  name: z.string(),
  shopName: z.string(),
  qty: z.number().int(),
  revenue: z.number(),
})
export type PromoStatsProduct = z.infer<typeof PromoStatsProductSchema>

export const PromoStatsShopSchema = z.object({
  shopId: z.string(),
  name: z.string(),
  orders: z.number().int(),
  qty: z.number().int(),
  revenue: z.number(),
})
export type PromoStatsShop = z.infer<typeof PromoStatsShopSchema>

export const PromoStatsSellerSchema = z.object({
  userId: z.string(),
  shopId: z.string(),
  shopName: z.string(),
  email: z.string(),
  phone: z.string(),
  onboardingStatus: z.string(),
  registered: z.boolean(),
  approved: z.boolean(),
  attributedAt: z.string(),
  registeredAt: z.string(),
  approvedAt: z.string().nullable(),
  source: z.string(),
  productsPublished: z.number().int(),
  productsTotal: z.number().int(),
  orders: z.number().int(),
  qty: z.number().int(),
  gmv: z.number(),
  commission: z.number(),
})
export type PromoStatsSeller = z.infer<typeof PromoStatsSellerSchema>

export const PromoStatsDaySchema = z.object({
  day: z.string(),
  visits: z.number().int(),
  applies: z.number().int(),
  orders: z.number().int(),
  revenue: z.number(),
  discount: z.number(),
  registrations: z.number().int(),
  approvals: z.number().int(),
  sellerGmv: z.number(),
})
export type PromoStatsDay = z.infer<typeof PromoStatsDaySchema>

export const PromoStatsTotalsSchema = z.object({
  visits: z.number().int(),
  applies: z.number().int(),
  orders: z.number().int(),
  buyers: z.number().int(),
  newBuyers: z.number().int(),
  repeatBuyers: z.number().int(),
  gmv: z.number(),
  discount: z.number(),
  revenue: z.number(),
  avgCheck: z.number(),
  refunds: z.number().int(),
  refundAmount: z.number(),
  itemsSold: z.number().int(),
  /** Заказы / применения кода в корзине; null — применений не было. */
  conversion: z.number().nullable(),
  /** Покупатели, пришедшие по коду/ссылке (регистрация или первый заказ). */
  attractedBuyers: z.number().int(),
  /** Все засчитанные покупки привлечённых покупателей за период (с кодом и без). */
  attractedGmv: z.number(),
  sellersRegistered: z.number().int(),
  sellersApproved: z.number().int(),
  sellersStarted: z.number().int(),
  sellerProducts: z.number().int(),
  sellerOrders: z.number().int(),
  sellerQty: z.number().int(),
  sellerGmv: z.number(),
  sellerCommission: z.number(),
  reward: z.number(),
})
export type PromoStatsTotals = z.infer<typeof PromoStatsTotalsSchema>

export const PromoStatsSchema = z.object({
  code: z.string(),
  audience: PromoAudienceSchema,
  from: z.string(),
  to: z.string(),
  totals: PromoStatsTotalsSchema,
  daily: z.array(PromoStatsDaySchema),
  orders: z.array(PromoStatsOrderSchema),
  buyers: z.array(PromoStatsBuyerSchema),
  products: z.array(PromoStatsProductSchema),
  shops: z.array(PromoStatsShopSchema),
  sellers: z.array(PromoStatsSellerSchema),
})
export type PromoStats = z.infer<typeof PromoStatsSchema>

export const PartnerStatsSchema = z.object({
  from: z.string(),
  to: z.string(),
  totals: PromoStatsTotalsSchema,
  byPromo: z.array(
    z.object({
      code: z.string(),
      audience: PromoAudienceSchema,
      totals: PromoStatsTotalsSchema,
    }),
  ),
})
export type PartnerStats = z.infer<typeof PartnerStatsSchema>

/** Настройки партнёрской программы. */
export const PartnerSettingsSchema = z.object({
  /** Сколько дней после регистрации продавец может сам указать промокод. */
  sellerBindDays: z.number().int().min(0).max(3650),
})
export type PartnerSettings = z.infer<typeof PartnerSettingsSchema>

/** Проверка кода «на лету» (регистрация продавца, ссылка витрины). */
export const PromoCheckResultSchema = z.object({
  valid: z.boolean(),
  code: z.string(),
  audience: PromoAudienceSchema.nullable(),
  partnerName: z.string().nullable(),
  reason: z
    .enum(["not_found", "inactive", "scheduled", "expired", "exhausted", "wrong_audience"])
    .nullable(),
  message: z.string(),
})
export type PromoCheckResult = z.infer<typeof PromoCheckResultSchema>

/** Промокод партнёра, привязанный к магазину (кабинет продавца). */
export const SellerPartnerPromoSchema = z.object({
  bound: z
    .object({
      code: z.string(),
      partnerName: z.string().nullable(),
      boundAt: z.string(),
      source: z.string(),
    })
    .nullable(),
  /** Можно ли сейчас указать код самому продавцу. */
  canBind: z.boolean(),
  /** До какого момента можно указать (null — пока не пройдена модерация). */
  bindUntil: z.string().nullable(),
})
export type SellerPartnerPromo = z.infer<typeof SellerPartnerPromoSchema>

// ── Механики промокодов П2 (этап 8) ─────────────────────────────────────────

/**
 * Промокод магазина (fd-promo-03): продавец заводит его в кабинете для своих
 * товаров. Магазин берётся из токена, товары — только свои; скидка за счёт
 * магазина. Процент — долей (0.10 = 10 %), как у кодов площадки.
 */
export const SellerPromoCodeInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  kind: z.enum(["percent", "fixed"]),
  value: z.number().positive(),
  minSubtotal: z.number().nonnegative().default(0),
  maxUses: z.number().int().positive().nullable().default(null),
  perUserLimit: z.number().int().min(1).nullable().default(1),
  active: z.boolean().default(true),
  description: z.string().max(500).default(""),
  startsAt: z.string().nullable().default(null),
  expiresAt: z.string().nullable().default(null),
  /** Только эти товары магазина; пусто — все товары магазина. */
  productIds: z.array(z.string()).max(500).default([]),
  /** Показывать покупателям в «Мои купоны». */
  isPublic: z.boolean().default(false),
  /** Только для новых пользователей платформы (нет оплаченных заказов на OSMIX). */
  firstOrderOnly: z.boolean().optional(),
  /** Только для тех, кто ещё не покупал в этом магазине. */
  newToShopOnly: z.boolean().optional(),
})
export type SellerPromoCodeInput = z.infer<typeof SellerPromoCodeInputSchema>

export const SellerPromoCodePatchSchema = SellerPromoCodeInputSchema.omit({ code: true })
  .partial()
  .extend({ archived: z.boolean().optional() })
export type SellerPromoCodePatch = z.infer<typeof SellerPromoCodePatchSchema>

/** Правило генерации персональных кодов (fd-promo-12). */
export const PersonalOfferRuleSchema = z.object({
  enabled: z.boolean().default(false),
  kind: z.enum(["percent", "fixed", "free_delivery"]).default("percent"),
  /** percent — доля (0.10 = 10 %), fixed — рубли, free_delivery — 0. */
  value: z.number().nonnegative().default(0.1),
  minSubtotal: z.number().nonnegative().default(0),
  /** Сколько дней действует выданный код. */
  validDays: z.number().int().min(1).max(365).default(7),
  /** Период «один код на покупателя по правилу», дней. */
  periodDays: z.number().int().min(1).max(365).default(30),
  /** Порог правила, дней: брошенная корзина старше N дней / нет заказов N дней. */
  thresholdDays: z.number().int().min(1).max(365).default(3),
})
export type PersonalOfferRule = z.infer<typeof PersonalOfferRuleSchema>

export const PERSONAL_OFFER_RULES = ["abandoned_cart", "favorite_price_drop", "inactive_buyer"] as const
export const PersonalOfferRuleKeySchema = z.enum(PERSONAL_OFFER_RULES)
export type PersonalOfferRuleKey = z.infer<typeof PersonalOfferRuleKeySchema>

export const PersonalOffersSettingsSchema = z.object({
  rules: z.object({
    abandoned_cart: PersonalOfferRuleSchema,
    favorite_price_drop: PersonalOfferRuleSchema,
    inactive_buyer: PersonalOfferRuleSchema,
  }),
})
export type PersonalOffersSettings = z.infer<typeof PersonalOffersSettingsSchema>

export const PersonalOffersRunResultSchema = z.object({
  created: z.number().int(),
  byRule: z.record(z.string(), z.number().int()),
  ranAt: z.string(),
})
export type PersonalOffersRunResult = z.infer<typeof PersonalOffersRunResultSchema>

/** Купон в «Мои купоны» покупателя (fd-promo-34). */
export const BuyerCouponSchema = z.object({
  code: z.string(),
  kind: z.enum(["percent", "fixed", "free_delivery"]),
  value: z.number(),
  minSubtotal: z.number(),
  description: z.string(),
  startsAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  /** Откуда купон у покупателя: персональный, сохранённый, общедоступный. */
  source: z.enum(["personal", "saved", "public"]),
  /** Код магазина — название магазина; код площадки — null. */
  shopName: z.string().nullable(),
  /** Сколько раз ещё можно применить этому покупателю (null — без лимита). */
  usesLeft: z.number().int().nullable(),
  /** Есть ли ограничение по товарам/категориям/магазинам. */
  restricted: z.boolean(),
  // ── «Мои промокоды» и лента корзины (27.10.2026, PDF п.1), аддитивно ──
  /** Название для карточки: описание кода, без него - сам код. */
  title: z.string().default(""),
  /** Крупная строка карточки: «-20%», «-500 руб.», «Бесплатная доставка». */
  headline: z.string().default(""),
  /** «до 31 октября» (по Москве); бессрочный код - null. */
  expiresLabel: z.string().nullable().default(null),
  /** Условия по-русски, по одному на строку: «Только для первого заказа на OSMIX», «От 3 000 руб.». */
  conditions: z.array(z.string()).default([]),
  /** Только для тех, у кого нет ни одного оплаченного заказа на площадке. */
  firstOrderOnly: z.boolean().default(false),
  /** Только для тех, кто ещё не покупал в магазине (магазинах) кода. */
  newToShopOnly: z.boolean().default(false),
  /** Магазин-владелец кода магазина; код площадки - null. */
  shopId: z.string().nullable().default(null),
  /**
   * Состояние кода для этого покупателя «на сейчас». В `available` всегда
   * active; в `ended` - почему код больше не применить.
   */
  status: z.enum(["active", "scheduled", "expired", "exhausted", "used", "inactive", "not_eligible"]).default("active"),
  /** Короткое пояснение к статусу (для ended): «Срок действия кода истёк». */
  statusMessage: z.string().nullable().default(null),
})
export type BuyerCoupon = z.infer<typeof BuyerCouponSchema>

export const BuyerCouponHistoryItemSchema = z.object({
  code: z.string(),
  orderId: z.string(),
  orderNumber: z.number().nullable(),
  amount: z.number(),
  usedAt: z.string(),
  /** Погашение откатано отменой заказа — код вернулся в «Доступные». */
  reverted: z.boolean(),
})
export type BuyerCouponHistoryItem = z.infer<typeof BuyerCouponHistoryItemSchema>

export const BuyerCouponsSchema = z.object({
  available: z.array(BuyerCouponSchema),
  history: z.array(BuyerCouponHistoryItemSchema),
  /**
   * Сохранённые коды, которые больше не применить (истёк срок, исчерпан,
   * выключен, уже использован, не подходит этому покупателю). Истёкшие
   * показываются 30 дней после окончания, потом пропадают сами - фильтр при
   * чтении, без фоновой задачи.
   */
  ended: z.array(BuyerCouponSchema).default([]),
})
export type BuyerCoupons = z.infer<typeof BuyerCouponsSchema>

/**
 * Машинный код отказа в промокоде (27.10.2026). Приходит в ответе ошибки
 * `error.details.reason` (при `error.details.field = "promo"`) и в карточке
 * ленты корзины. Текст для человека - `error.message` / `message`.
 */
export const PromoRejectReasonSchema = z.enum([
  "not_found",
  "inactive",
  "scheduled",
  "expired",
  "exhausted",
  "already_used",
  "seller_code",
  "login_required",
  "first_order_only",
  "new_to_shop_only",
  "min_subtotal",
  "not_applicable",
])
export type PromoRejectReason = z.infer<typeof PromoRejectReasonSchema>

/**
 * Карточка ленты промокодов корзины (`GET /cart/:id/promos`): код
 * покупателя (персональный, сохранённый) или общедоступный код площадки и
 * магазинов из корзины - и можно ли применить его к этой корзине сейчас.
 */
export const CartPromoCardSchema = BuyerCouponSchema.extend({
  /** Код уже применён к этой корзине. */
  applied: z.boolean(),
  /** Применится к корзине прямо сейчас (кнопка «Применить» активна). */
  applicable: z.boolean(),
  /** Почему не применится; null - применится или уже применён. */
  reason: PromoRejectReasonSchema.nullable(),
  /** Текст причины, тот же, что вернёт `POST /cart/:id/promo`. */
  message: z.string().nullable(),
  /** reason = min_subtotal: сколько рублей не хватает до порога. */
  missing: z.number().nullable(),
})
export type CartPromoCard = z.infer<typeof CartPromoCardSchema>

export const CartPromoFeedSchema = z.object({ items: z.array(CartPromoCardSchema) })
export type CartPromoFeed = z.infer<typeof CartPromoFeedSchema>
